import React from "react";
import cn from "classnames";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ShellIcon } from "./icons";
import styles from "./appShell.module.css";

const hasActive = (group, activeId) => group.items.some(item => item.id === activeId);

/** Whether a titled group is open: what the member chose, else open only when it holds the current page. */
export const isGroupOpen = (group, activeId, openMap) => {
  if (!group.label) {
    return true;
  }

  return openMap[group.key] ?? hasActive(group, activeId);
};

const Badge = ({ count, className }) => <span className={cn(styles.badge, className)}>{count}</span>;

/**
 * The sidebar's links. `accordion` (the sidebar) folds each titled group to one row; the first group never folds. Otherwise
 * (the drawer) every group is open and its title is a plain label.
 *
 * @param {Object} props
 * @param {Array} props.nav the groups from buildNav()
 * @param {string} props.activeId the active page id
 * @param {Object<string, boolean>} [props.openMap] the member's open/closed choice per group key (accordion only)
 * @param {Function} [props.onToggle] (groupKey, open) → void (accordion only)
 * @param {Function} [props.onNavigate] called when a link is followed
 * @param {number} props.inboxCount unread messages for the Inbox badge
 * @param {boolean} [props.accordion]
 */
export const NavList = ({ nav, activeId, openMap, onToggle, onNavigate, inboxCount, accordion }) => {
  const { t } = useTranslation();

  return (
    <nav aria-label={t("dashboard_shell_nav_label")} className={styles.nav}>
      {nav.map(group => {
        const open = accordion ? isGroupOpen(group, activeId, openMap) : true;
        const holdsActive = hasActive(group, activeId);

        return (
          <React.Fragment key={group.key}>
            {group.label && accordion && (
              <button
                type="button"
                className={cn(styles.groupToggle, holdsActive && styles.groupOn)}
                aria-expanded={open}
                onClick={() => onToggle(group.key, !open)}
              >
                <ShellIcon name={group.icon} size={16} />
                <span className={styles.groupTitle}>{t(group.label)}</span>
                <span className={cn(styles.chevron, !open && styles.chevronFolded)}>
                  <ShellIcon name="chevron" size={16} />
                </span>
              </button>
            )}
            {group.label && !accordion && (
              <span className={cn(styles.groupLabel, holdsActive && styles.groupOn)}>
                <ShellIcon name={group.icon} size={16} />
                {t(group.label)}
              </span>
            )}
            {open &&
              group.items.map(item => (
                <Link
                  key={item.id}
                  to={item.to}
                  onClick={onNavigate}
                  className={cn(styles.item, !accordion && styles.itemDrawer, item.id === activeId && styles.itemActive)}
                  aria-current={item.id === activeId ? "page" : undefined}
                >
                  <span className={styles.itemLabel}>{t(item.label)}</span>
                  {item.count && inboxCount > 0 && <Badge count={inboxCount} />}
                </Link>
              ))}
          </React.Fragment>
        );
      })}
    </nav>
  );
};
